/**
 * External Imports
 */
import { format } from 'date-fns';
import { connect, useDispatch } from 'react-redux';
import { FC, useEffect, useMemo, useState } from 'react';
import { RootState } from '../../../redux/store';
/**
 * Internal Imports
 */
import ComplianceMetricChart from '../../../components/customMetricCharts/patient/ComplianceMetricChart';
import RecognitionMetricChart from '../../../components/customMetricCharts/patient/RecognitionMetricChart';
import CoachingMetricChart from '../../../components/customMetricCharts/patient/CoachingMetricChart';
import StoplightMetricChart from '../../../components/customMetricCharts/patient/StoplightMetricChart';
import SummaryReportHeader from '../../../components/searchHeader/summaryReportHeader';

import { date as dt } from '../../../utils/helpers';
import TaskMetricChart from '../../../components/customMetricCharts/patient/TaskMetricChart';
import {
  useFeedbackApi,
  useIssuesApi,
  useStoplightsApi
} from '../../../utils/api';
import { getComplianceReport, setUserSection } from '../../../redux/actions';
import NoDataSplashView from '../../../components/shared/table/noDataSplashView';
import Loading from '../../../components/shared/Loading';
/**
 * Global Definition Imports
 */
import { AuthUserProps, Filter } from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  CTSMetricChartsContainer,
  SummaryReportContainer,
  TSMetricChartsContainer
} from '../styles/ReportsViews.styles';

interface PatientSummaryReportProps {
  authorizedUser: AuthUserProps;
  patientSummaryReportFilter: Filter;
  selectedUnits: { id: string; unit: string }[];
  isIssuesLoading: boolean;
  isFeedbackLoading: boolean;
  isComplianceLoading: boolean;
  isStoplightLoading: boolean;
}

const PatientSummaryReport: FC<PatientSummaryReportProps> = ({
  authorizedUser,
  patientSummaryReportFilter,
  selectedUnits,
  isIssuesLoading,
  isFeedbackLoading,
  isComplianceLoading,
  isStoplightLoading
}) => {
  const dispatch = useDispatch();
  const [initialRender, setInitialRender] = useState(false);
  const today = new Date();

  const dateRange = {
    firstDayOfMonth: format(
      new Date(today.getFullYear(), today.getMonth(), 1),
      'yyyy-MM-dd'
    ),
    todayDate: format(
      new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      'yyyy-MM-dd'
    )
  };

  const isAnyChartsLoading = useMemo(
    () =>
      isIssuesLoading ||
      isFeedbackLoading ||
      isComplianceLoading ||
      isStoplightLoading ||
      !initialRender,
    [
      isIssuesLoading,
      isFeedbackLoading,
      isComplianceLoading,
      isStoplightLoading,
      initialRender
    ]
  );

  useEffect(() => {
    // summary report filter is persistent, so this triggers a re-fetch just in case any thing changed
    setInitialRender(true);
  }, []);

  useEffect(() => {
    const issueFeedbackParams = {
      authorizedUser,
      searchTerm: '',
      selectedDelegate: '',
      selectedUnits,
      selectedEmpDepartments: [],
      sortDir: '',
      sortKey: '',
      summaryView: 'patient'
    };
    dispatch(setUserSection('reports'));
    dispatch(
      getComplianceReport({
        facilityId: authorizedUser?.facilityId,
        startday:
          patientSummaryReportFilter.startDate?.replace(/-/g, '') ??
          dateRange.todayDate.replace(/-/g, ''),
        endday: dt
          .addDayToDayOfYearDate(
            patientSummaryReportFilter.endDate ?? dateRange.todayDate
          )
          .replace(/-/g, '')
      })
    );

    dispatch(
      useIssuesApi({
        ...issueFeedbackParams,
        currentIssuePage: 1,
        issueFilter: patientSummaryReportFilter
      })
    );
    dispatch(
      useFeedbackApi({
        ...issueFeedbackParams,
        currentFeedbackPage: 1,
        feedbackFilter: {
          ...patientSummaryReportFilter,
          taskSubType: 'coaching'
        }
      })
    );
    dispatch(
      useFeedbackApi({
        ...issueFeedbackParams,
        currentFeedbackPage: 1,
        feedbackFilter: {
          ...patientSummaryReportFilter,
          taskSubType: 'recognition'
        }
      })
    );
    dispatch(
      useStoplightsApi({
        ...issueFeedbackParams,
        stoplightFilter: patientSummaryReportFilter,
        currentStoplightPage: 1
      })
    );
  }, [authorizedUser, patientSummaryReportFilter, selectedUnits]);

  return (
    <SummaryReportContainer>
      <SummaryReportHeader />
      {selectedUnits.length === 0 ? (
        <NoDataSplashView type="patientSummaryReport" />
      ) : isAnyChartsLoading ? (
        <Loading centered={false} />
      ) : (
        <CTSMetricChartsContainer>
          <ComplianceMetricChart />
          <TSMetricChartsContainer>
            <TaskMetricChart />
            <StoplightMetricChart />
            <RecognitionMetricChart />
            <CoachingMetricChart />
          </TSMetricChartsContainer>
        </CTSMetricChartsContainer>
      )}
    </SummaryReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    selectedUnits: state.ConfigReducer.selectedUnits,
    isIssuesLoading: state.TaskReducer.loadingIssuesByUnitIdsOrEmployeeList,
    isFeedbackLoading: state.TaskReducer.loadingFeedbackByUnitIds,
    isComplianceLoading: state.ReportReducer.loading,
    isStoplightLoading: state.StoplightReducer.loading
  };
};

export default connect(mapReduxStateToProps)(PatientSummaryReport);
